import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import IndustriesPanels from "@/components/marketing/IndustriesPanels";
import { FinalCTA } from "@/components/marketing/Sections";
import { industries, services } from "@/lib/data";
import Breadcrumbs from "@/components/marketing/Breadcrumbs";
import SEO, { breadcrumbLd } from "@/components/SEO";
import { ArrowUpRight } from "lucide-react";

const INDUSTRY_SERVICES = {
    "restaurants-cafes": ["new-business-technology-setup", "digital-signage-menu-boards", "pos-workstation-coordination", "commercial-audio-systems", "business-wifi-networking"],
    "hotels-hospitality": ["commercial-av-installation", "commercial-audio-systems", "business-wifi-networking", "digital-signage-menu-boards"],
    "medical-dental-clinics": ["new-business-technology-setup", "business-wifi-networking", "cable-equipment-organization", "pos-workstation-coordination"],
    "retail-commercial": ["digital-signage-menu-boards", "commercial-audio-systems", "pos-workstation-coordination", "business-wifi-networking"],
    "new-construction-ti": ["new-business-technology-setup", "cable-equipment-organization", "business-wifi-networking", "commercial-av-installation"],
    "property-managers-developers": ["managed-support-plans", "cable-equipment-organization", "technology-troubleshooting-upgrades"],
};

export function IndustriesIndex() {
    return (
        <MarketingLayout>
            <SEO
                title="Industries · Commercial Technology for Metro Vancouver Businesses"
                description="Commercial technology installation and support tuned to restaurants, hotels, medical & dental clinics, retail, new tenant improvements and property managers across the Lower Mainland."
            />
            <section className="pt-[132px] pb-16 border-b border-line">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10">
                    <Breadcrumbs items={[{ name: "Home", href: "/" }, { name: "Industries", href: "/industries" }]} />
                    <p className="eyebrow mb-6">Industries</p>
                    <h1 className="font-display text-5xl md:text-7xl tracking-tight leading-[0.95] max-w-4xl">Technology tuned to your industry.</h1>
                    <p className="mt-6 text-muted max-w-2xl leading-relaxed">We shape scopes around how your business actually runs — clinic workflows, restaurant service, hotel operating hours, retail traffic.</p>
                </div>
            </section>
            <IndustriesPanels />
            <FinalCTA />
        </MarketingLayout>
    );
}

export default function IndustryDetail() {
    const { slug } = useParams();
    const industry = industries.find((i) => i.slug === slug);
    if (!industry) return <Navigate to="/industries" replace />;

    const related = (INDUSTRY_SERVICES[slug] || []).map((s) => services.find((sv) => sv.slug === s)).filter(Boolean);

    return (
        <MarketingLayout>
            <SEO
                title={`${industry.title} · Commercial Technology · Metro Vancouver`}
                description={`${industry.blurb} Coordinated installation and ongoing support across the Lower Mainland.`}
                jsonLd={breadcrumbLd([
                    { name: "Home", href: "/" },
                    { name: "Industries", href: "/industries" },
                    { name: industry.title, href: `/industries/${slug}` },
                ])}
            />
            <section className="pt-[132px] pb-16 border-b border-line">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10">
                    <Breadcrumbs items={[{ name: "Home", href: "/" }, { name: "Industries", href: "/industries" }, { name: industry.title, href: `/industries/${slug}` }]} />
                    <div className="grid md:grid-cols-12 gap-10">
                        <div className="md:col-span-6">
                            <p className="eyebrow mb-6">Industry</p>
                            <h1 className="font-display text-5xl md:text-[80px] tracking-tight leading-[0.9]">{industry.title}</h1>
                            <p className="mt-8 text-lg text-ink/85 max-w-xl leading-relaxed">{industry.blurb}</p>
                            <p className="mt-6 text-muted leading-relaxed max-w-xl">Every engagement in this industry starts with a site walkthrough. We coordinate with your contractor, designer and any incumbent IT vendor so nothing falls between the cracks.</p>
                            <div className="mt-10 flex flex-wrap gap-3">
                                <Link to="/contact" className="inline-flex items-center gap-2 bg-ink text-bg px-6 py-4 text-sm hover:bg-accent transition-colors">Request a Site Assessment <ArrowUpRight className="w-4 h-4" /></Link>
                                <Link to="/services" className="inline-flex items-center gap-2 border border-ink text-ink px-6 py-4 text-sm hover:bg-ink hover:text-bg transition-colors">Explore services</Link>
                            </div>
                        </div>
                        <div className="md:col-span-6 relative aspect-[4/5] overflow-hidden bg-ink">
                            <img src={industry.image} alt={`${industry.title} — commercial interior reference`} className="absolute inset-0 w-full h-full object-cover" />
                        </div>
                    </div>
                </div>
            </section>

            {related.length > 0 && (
                <section className="border-b border-line bg-bg-alt">
                    <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-16 md:py-20">
                        <p className="eyebrow mb-6">Services we run for this industry</p>
                        <h2 className="font-display text-3xl md:text-5xl tracking-tight leading-[0.95] mb-10 max-w-3xl">Common scopes.</h2>
                        <div className="grid md:grid-cols-3 gap-6">
                            {related.map((rs) => (
                                <Link key={rs.slug} to={`/services/${rs.slug}`} className="group border border-line bg-bg p-6 hover:bg-ink hover:text-bg transition-colors duration-300">
                                    <div className="flex items-center justify-between mb-8">
                                        <span className="font-mono text-xs tracking-widest text-muted group-hover:text-bg/70">{rs.number}</span>
                                        <ArrowUpRight className="w-4 h-4" />
                                    </div>
                                    <h3 className="font-display text-xl md:text-2xl tracking-tight mb-2">{rs.title}</h3>
                                    <p className="text-sm text-muted group-hover:text-bg/70">{rs.short}</p>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            <FinalCTA />
        </MarketingLayout>
    );
}
